import axios from 'axios';
import config from '../config';
import { clearAdminAuth } from '../utils/auth';

// Helper to read cookies
function getCookie(name) {
  let cookieValue = null;
  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.substring(0, name.length + 1) === (name + '=')) {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
        break;
      }
    }
  }
  return cookieValue;
}

// 1. Auth Service Instance (Port 8001)
// Used for login, logout, refresh, and switching services
export const authApi = axios.create({
  baseURL: config.django.authBaseURL,
  timeout: 60000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 2. Core API Instance (Port 8000)
// Used for all business data and authenticated via JWT cookies
export const coreApi = axios.create({
  baseURL: config.django.baseURL,
  timeout: 60000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

const CSRF_COOKIE_NAME = () => config.django?.csrfCookieName || 'csrftoken';
const CSRF_HEADER_NAME = () => config.django?.csrfTokenHeader || 'X-CSRFToken';

// Helper for CSRF extraction — always overwrites so a rotated token is picked up
// on retried requests (e.g. after a JWT refresh).
const applyCsrf = (reqConfig) => {
  const csrfToken = getCookie(CSRF_COOKIE_NAME());

  if (csrfToken) {
    reqConfig.headers[CSRF_HEADER_NAME()] = csrfToken;
  }

  if (reqConfig.data instanceof FormData) {
    delete reqConfig.headers['Content-Type'];
  }
  return reqConfig;
};

// Apply CSRF to both instances
authApi.interceptors.request.use(applyCsrf, (err) => Promise.reject(err));
coreApi.interceptors.request.use(applyCsrf, (err) => Promise.reject(err));

// --- Core API 401/403 Interceptor Logic ---
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

/**
 * Returns true when a 403 is caused by an expired/invalid JWT rather than a
 * genuine "you are not allowed" permission error.
 *
 * Some Django JWT backends (e.g. djangorestframework-jwt, custom SimpleJWT
 * configs) return 403 instead of 401 for an expired access token. The
 * response body typically carries a detail string that mentions "JWT",
 * "expired", or "signature". We check for that rather than treating every
 * 403 as a token error (which would break real permission-denied responses).
 */
const isExpiredTokenError = (error) => {
  const detail = (error?.response?.data?.detail || '').toLowerCase();
  // Matches: "jwt error: signature has expired", "token is expired",
  //          "signature has expired", "authentication credentials were not provided", etc.
  return /jwt|token.*expir|expir.*token|signature.*expir|expir.*signature|not.*provided|invalid.*token|token.*invalid/i.test(detail);
};

coreApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Guard: error.config is undefined for network errors / cancelled requests.
    // Without this, accessing originalRequest._retry below throws a TypeError.
    if (!originalRequest) {
      return Promise.reject(error);
    }

    const status = error.response?.status;

    // Trigger refresh on:
    //   • 401 — standard "unauthenticated" response
    //   • 403 — only when the body indicates an expired/invalid JWT; a plain
    //           permission-denied 403 should NOT trigger a refresh attempt.
    const shouldRefresh =
      (status === 401 || (status === 403 && isExpiredTokenError(error))) &&
      !originalRequest._retry;

    if (shouldRefresh) {
      if (isRefreshing) {
        // Another refresh is already in flight — queue this request and replay
        // it once the refresh resolves (or reject it if the refresh fails).
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(() => coreApi(originalRequest))
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Refresh token lives in the HTTPOnly cookie — send empty body.
        await authApi.post(config.django.apiEndpoints.refresh, {});

        isRefreshing = false;
        processQueue(null);

        // Delete the stale CSRF header so applyCsrf re-reads the (possibly
        // rotated) cookie value on the retried request.
        delete originalRequest.headers[CSRF_HEADER_NAME()];

        return coreApi(originalRequest);
      } catch (refreshError) {
        isRefreshing = false;
        processQueue(refreshError);

        // Cleanup only auth-related session keys — clearAdminAuth covers both
        // admin and regular user keys without wiping unrelated app state.
        clearAdminAuth();

        if (!window.location.pathname.includes('/login')) {
          window.location.href = '/login';
        }

        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);
